import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import Breadcrumb from 'sushiweb/Breadcrumb/Breadcrumb';
import H5 from 'sushiweb/Typography/Heading/H5';
import grey from 'sushiweb/tokens/color/grey';
import Tooltip from 'sushiweb/Tooltip/Tooltip';
import Download from 'sushiweb/Icons/all/Download';
import red from 'sushiweb/tokens/color/red';

import {
  DashboardWrapper,
  TableContainer,
  StyledButton,
  BreadCrumbWrapper,
  TabWrapper,
  SearchContainerV2,
  SearchInput,
  StatusFilterAndExportCSVContainer,
  BreadcrumbHeaderWrapper,
} from '../common/styles';
import DataTables from '../common/components/DataTables';
import {
  paymentsCreditsHeadColumn,
  paymentsDebitsHeadColumn,
} from '../common/components/DataTables/components/headColumns';
import {
  checkValidExecutionDate,
  exportCSV,
  formatAmountInRupees,
  formatDateinDDMMMYYY,
  formatEventsAmountValue,
  formatReferenceIDForPayments,
  getCurrentDate,
  includesOption,
  indexOfOption,
  maskUptoLastNDigits,
  queryVariableValue,
  searchWithName,
} from '../../helpers';
import {
  PAYMENTS,
  applicableFlowTypes,
  currentFlowTypeEnum,
  eventsAmountEnum,
  paymentStatusEnum,
  sourceToCreditType,
  statusLabels,
  topupsStatusEnum,
} from '../common/components/constants';
import EmptyStateV2 from '../common/components/EmptyStateV2/EmptyStateV2';
import {
  emptyPaymentsStateTitle,
  paymentDetailsFailedInfo,
  paymentDetailsPendingInfo,
  paymentDetailsSuccessInfo,
} from '../../constants';
import OnlinePaymentModal from '../common/components/OnlinePaymentModal';
import {
  ActionComponentWrapper,
  CreditsWrapper,
  DebitWrapper,
  DebitsSectionHeaderWrapper,
  TableHeaderWrapper,
} from './styles';
import NotFound from '../common/components/NotFound';
import {
  DisabledShareUTRIcon,
  OnlinePaymentsIcon,
  RefreshIcon,
  ShareUTRIcon,
} from '../../components/SVGs/commonSVGs';
import StatusFilter from '../common/components/StatusFilter';
import { DASHBOARD_ROUTE, PAYMENTS_ROUTE } from '../../constants/routes';
import Invoices from './Invoices';
import PaymentUpdate from '../common/components/PaymentUpdate';

const Payments = ({
  currentFlow,
  fetchingPayments,
  getAllPayments,
  paymentsData,
  selectedWorkspace,
  getPaymentDebits,
  paymentDebitsData,
  fetchingPaymentDebits,
  isOnlinePaymentsEnabled,
  showWarningToast,
  showSuccessToast,
  showFailedToast,
  refreshAuth,
  refreshingAuth,
}) => {
  const debitsKeyField = ['event_id'];
  const paymentsDebits = paymentDebitsData;
  const [currentDebitsData, setCurrentDebitsData] = useState([]);
  const [searchDebitsResults, setSearchDebitsResults] = useState([]);
  const [currentDebitsPage, setCurrentDebitsPage] = useState(1);
  const [searchDebitEnteries, onDebitInputChange] = useState('');
  const [debitTypingTimeout, setDebitTypingTimeout] = useState(false);
  const [isDebitSearchClicked, setDebitIsSearchClicked] = useState(false);
  const [debitsDisabledSearch, setDebitsDisabledSearch] = useState(false);

  const [currentDebitFilters, setCurrentDebitFilters] = useState([]);
  const [filterDebitStatusOptions, setFilterDebitStatusOptions] = useState([
    {
      value: [4],
      label: 'SCHEDULED_AMOUNT_PARKED',
      disabled: true,
    },
    {
      value: [5],
      label: 'PROCESSING_IN_PROGRESS',
      disabled: true,
    },
    {
      value: [6],
      label: 'PARTIALLY_DONE',
      disabled: true,
    },
    {
      value: [7],
      label: 'FAILED',
      disabled: true,
    },
    {
      value: [8, 9, 10, 11, 12],
      label: 'SUCCESS',
      disabled: true,
    },
  ]);
  const filterDebitFunc = val => {
    let flag = false;
    currentDebitFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };
  const statusDebitInitiatedFilter = val => {
    if (val.total_amount.value !== 0) {
      return true;
    }
    return false;
  };

  const enableCurrentDebitFilters = availableStatuses => {
    for (let i = 0; i < filterDebitStatusOptions.length; i += 1) {
      for (let j = 0; j < filterDebitStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterDebitStatusOptions[i].value[j])) {
          filterDebitStatusOptions[i].disabled = false;
          break;
        } else filterDebitStatusOptions[i].disabled = true;
      }
    }
    setFilterDebitStatusOptions(filterDebitStatusOptions);
  };

  const handleDebitStatusSelect = val => {
    const newCurrentFilters = [...currentDebitFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentDebitFilters(newCurrentFilters);
  };
  const clearDebitFilters = () => {
    setCurrentDebitFilters([]);
  };
  const exportCreditsSearchResultCSV = () => {
    const headColumns = [
      { key: 'created_on_val', value: 'Date' },
      { key: 'source_val', value: 'Payment type' },
      { key: 'reference_id_val', value: 'UTR/Reference no.' },
      { key: 'amount_val', value: 'Amount' },
      { key: 'status', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchCreditsResults.forEach(data => {
      const labelledData = {};
      labelledData.created_on_val = formatDateinDDMMMYYY(data.created_on);
      labelledData.source_val = sourceToCreditType(data.source);
      labelledData.reference_id_val = formatReferenceIDForPayments(data);
      labelledData.amount_val = formatAmountInRupees(String(data.amount));
      labelledData.status = statusLabels[paymentStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `Payment Credits - ${getCurrentDate()}`,
      headColumns,
    );
  };
  const downloadCreditsCSVStyle = {
    padding: '15px 20px 10px 0px',
    cursor: 'pointer',
  };

  const [currentCreditFilters, setCurrentCreditFilters] = useState([]);
  const [filterCreditStatusOptions, setFilterCreditStatusOptions] = useState([
    {
      value: [0, 6],
      label: 'PENDING',
      disabled: true,
    },
    {
      value: [1, 4],
      label: 'SUCCESS',
      disabled: true,
    },
    {
      value: [2],
      label: 'DECLINED',
      disabled: true,
    },
    {
      value: [3, 5],
      label: 'FAILED',
      disabled: true,
    },
    {
      value: [7, 8],
      label: 'REFUNDED',
      disabled: true,
    },
  ]);
  const filterCreditFunc = val => {
    let flag = false;
    currentCreditFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };
  const enableCurrentCreditFilters = availableStatuses => {
    for (let i = 0; i < filterCreditStatusOptions.length; i += 1) {
      for (let j = 0; j < filterCreditStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterCreditStatusOptions[i].value[j])) {
          filterCreditStatusOptions[i].disabled = false;
          break;
        } else filterCreditStatusOptions[i].disabled = true;
      }
    }
    setFilterCreditStatusOptions(filterCreditStatusOptions);
  };
  const handleCreditStatusSelect = val => {
    const newCurrentFilters = [...currentCreditFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentCreditFilters(newCurrentFilters);
  };
  const clearCreditFilters = () => {
    setCurrentCreditFilters([]);
  };
  const exportDebitsSearchResultCSV = () => {
    const headColumns = [
      { key: 'execution_date_val', value: 'Top-up date' },
      { key: 'program_name_val', value: 'Meal plan' },
      { key: 'disbursed_amount_value', value: 'Amount topped-up' },
      { key: 'pending_amount_value', value: 'Amount pending' },
      { key: 'released_amount_value', value: 'Amount released' },
      { key: 'ref_id_val', value: 'Ref. ID' },
      { key: 'status_val', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchDebitsResults.forEach(data => {
      const labelledData = {};
      labelledData.execution_date_val = checkValidExecutionDate(
        data.execution_date,
      )
        ? formatDateinDDMMMYYY(data.execution_date)
        : ' - ';
      labelledData.program_name_val = data.program_name;
      labelledData.disbursed_amount_value = formatAmountInRupees(
        String(data.disbursed_amount.value),
      );

      labelledData.pending_amount_value = formatEventsAmountValue(
        data,
        eventsAmountEnum.PENDING,
        true,
      );
      labelledData.released_amount_value = formatEventsAmountValue(
        data,
        eventsAmountEnum.RELEASED,
        true,
      );
      labelledData.ref_id_val = maskUptoLastNDigits(data.event_id, 6);
      labelledData.status_val = statusLabels[topupsStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `Payment Debits - ${getCurrentDate()}`,
      headColumns,
    );
  };
  const downloadDebitsCSVStyle = {
    padding: '15px 20px 10px 0px',
    cursor: 'pointer',
  };

  const creditsKeyField = ['reference_id'];
  const { payments = [] } = paymentsData;
  const emptyPayments = !payments;
  const [currentCreditsData, setCurrentCreditsData] = useState([]);
  const [searchCreditsResults, setSearchCreditsResults] = useState([]);
  const [currentCreditsPage, setCurrentCreditsPage] = useState(1);
  const [searchCreditEnteries, onCreditInputChange] = useState('');
  const [creditsTypingTimeout, setCreditsTypingTimeout] = useState(false);
  const [isCreditSearchClicked, setCreditIsSearchClicked] = useState(false);
  const [paymentUpdateModal, setPaymentUpdateModal] = useState(false);
  const [onlinePaymentModal, setOnlinePaymentModal] = useState(false);
  const [isOrderIDpresent, orderIDValue] = queryVariableValue('order_id');
  const [currentPaymentStatus, setCurrentPaymentStatus] = useState(-1);
  const [noUnpaidInvocies, setNoUnpaidInvocies] = useState(true);
  const [creditsSearchDisabled, setCreditsSearchDisabled] = useState(false);
  const paymentCreditsFilter = val => {
    if (
      val.payment_type !== 0 &&
      val.status !== paymentStatusEnum.VOID_FAILED
    ) {
      return true;
    }
    return false;
  };

  const refreshAllPayments = () => {
    refreshAuth();
  };

  useEffect(() => {
    if (isOrderIDpresent && currentPaymentStatus !== -1) {
      if (
        currentPaymentStatus === paymentStatusEnum.INITIATED_PENDING ||
        currentPaymentStatus === paymentStatusEnum.PENDING
      )
        showWarningToast(paymentDetailsPendingInfo);
      if (
        currentPaymentStatus === paymentStatusEnum.SUCCESS ||
        currentPaymentStatus === paymentStatusEnum.APPROVED_SUCCESS
      )
        showSuccessToast(paymentDetailsSuccessInfo);
      if (
        currentPaymentStatus === paymentStatusEnum.FAILED ||
        currentPaymentStatus === paymentStatusEnum.DECLINED
      )
        showFailedToast(paymentDetailsFailedInfo);
    }
  }, [isOrderIDpresent, currentPaymentStatus]);

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getAllPayments({
        workspaceId: selectedWorkspace.workspace_id,
        flowType: currentFlow,
      });
  }, [selectedWorkspace, currentFlow]);

  useEffect(() => {
    if (
      selectedWorkspace &&
      selectedWorkspace.workspace_id &&
      currentFlow === currentFlowTypeEnum.FLOW_TYPE_PPI
    )
      getPaymentDebits({
        workspaceId: selectedWorkspace.workspace_id,
        flowType: currentFlow,
      });
  }, [selectedWorkspace, currentFlow]);

  useEffect(() => {
    if (!fetchingPayments) {
      if (payments) {
        payments.sort(
          (a, b) => new Date(b.created_on) - new Date(a.created_on),
        );
        const filtererdCreditPayments = payments.filter(paymentCreditsFilter);
        if (filtererdCreditPayments.length === 0) {
          setCreditsSearchDisabled(true);
        }
        setSearchCreditsResults(filtererdCreditPayments);
        setCurrentCreditsData(filtererdCreditPayments.slice(0, 5));
      }
    }
  }, [fetchingPayments]);

  useEffect(() => {
    if (creditsTypingTimeout) {
      clearTimeout(creditsTypingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingPayments) {
        const arr = searchWithName(
          payments,
          searchCreditEnteries,
          creditsKeyField,
        );
        if (arr) {
          const creditPaymentArr = arr.filter(paymentCreditsFilter);
          const availableStatuses = [];
          creditPaymentArr.forEach(data => {
            if (data.reference_id === orderIDValue) {
              setCurrentPaymentStatus(data.status);
            }
            if (!availableStatuses.includes(data.status)) {
              availableStatuses.push(data.status);
            }
          });
          enableCurrentCreditFilters(availableStatuses);
          if (currentCreditFilters.length !== 0) {
            const filteredSearchArr = creditPaymentArr.filter(filterCreditFunc);
            setSearchCreditsResults(filteredSearchArr);
          } else {
            setSearchCreditsResults(creditPaymentArr);
          }
        }
        handleCreditsPageChange(1);
      }
    }, 500);

    return () => setCreditsTypingTimeout(timeout);
  }, [searchCreditEnteries, currentCreditFilters, fetchingPayments]);

  useEffect(() => {
    if (searchCreditsResults) {
      const start = currentCreditsPage * 5 - 5;
      const end = currentCreditsPage * 5;
      const rangeData = searchCreditsResults.slice(start, end);
      setCurrentCreditsData(rangeData);
    }
  }, [currentCreditsPage, searchCreditsResults]);

  const handleCreditsPageChange = page => {
    setCurrentCreditsPage(page);
  };

  useEffect(() => {
    if (!fetchingPaymentDebits) {
      if (paymentsDebits) {
        paymentsDebits.sort(
          (a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date),
        );
        const filtererdPaymentDebits = paymentsDebits.filter(
          statusDebitInitiatedFilter,
        );
        if (filtererdPaymentDebits.length === 0) {
          setDebitsDisabledSearch(true);
        }
        // Disable search here
        setSearchDebitsResults(filtererdPaymentDebits);
        setCurrentDebitsData(filtererdPaymentDebits.slice(0, 5));
      }
    }
  }, [fetchingPaymentDebits]);

  useEffect(() => {
    if (debitTypingTimeout) {
      clearTimeout(debitTypingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingPaymentDebits) {
        const arr = searchWithName(
          paymentsDebits,
          searchDebitEnteries,
          debitsKeyField,
        );
        const initiatedFilteredArr = arr.filter(statusDebitInitiatedFilter);
        const availableStatuses = [];
        initiatedFilteredArr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentDebitFilters(availableStatuses);
        if (currentDebitFilters.length !== 0) {
          const filteredSearchArr = initiatedFilteredArr.filter(
            filterDebitFunc,
          );
          setSearchDebitsResults(filteredSearchArr);
        } else {
          setSearchDebitsResults(initiatedFilteredArr);
        }
        handleDebitsPageChange(1);
      }
    }, 500);

    return () => setDebitTypingTimeout(timeout);
  }, [searchDebitEnteries, currentDebitFilters, fetchingPaymentDebits]);

  useEffect(() => {
    if (searchDebitsResults) {
      const start = currentDebitsPage * 5 - 5;
      const end = currentDebitsPage * 5;
      const rangeData = searchDebitsResults.slice(start, end);
      setCurrentDebitsData(rangeData);
    }
  }, [currentDebitsPage, searchDebitsResults]);

  const handleDebitsPageChange = page => {
    setCurrentDebitsPage(page);
  };
  const handleSortCredits = (column, sortDirection) => {
    setSearchCreditsResults(
      orderBy(searchCreditsResults, column.selector, sortDirection),
    );
  };
  const handleSortDebits = (column, sortDirection) => {
    setSearchDebitsResults(
      orderBy(searchDebitsResults, column.selector, sortDirection),
    );
  };
  const closePaymentUpdate = () => {
    setPaymentUpdateModal(false);
  };

  const openPaymentUpdate = () => {
    setPaymentUpdateModal(true);
  };

  const closeOnlinePaymentModal = () => {
    setOnlinePaymentModal(false);
  };

  const openOnlinePaymentModal = () => {
    setOnlinePaymentModal(true);
  };

  const ActionComponent = () => {
    return (
      <>
        {currentFlow === currentFlowTypeEnum.FLOW_TYPE_PPI ? (
          <ActionComponentWrapper>
            <div>
              <StyledButton
                icon={<RefreshIcon />}
                iconAlign='right'
                onClick={refreshAllPayments}
                style={{
                  fontWeight: 400,
                  fontSize: '14px',
                  color: 'black',
                  backgroundColor: 'white',
                  border: `0.5px solid ${grey.z200}`,
                }}
              >
                Refresh
              </StyledButton>
              <StyledButton
                icon={<OnlinePaymentsIcon />}
                iconAlign='right'
                onClick={openOnlinePaymentModal}
                style={{ fontWeight: 400, fontSize: '14px' }}
              >
                Pay online
              </StyledButton>
            </div>
          </ActionComponentWrapper>
        ) : (
          <ActionComponentWrapper>
            {noUnpaidInvocies ? (
              <div style={{ marginTop: '0px' }}>
                <Tooltip
                  position='bottom'
                  label='There are no pending statements'
                  appearance='light'
                  size='big'
                  style={{
                    fontSize: '12px',
                    opacity: 0.8,
                    padding: '10px',
                    width: '100px',
                    marginLeft: '50px',
                  }}
                >
                  <StyledButton
                    icon={<DisabledShareUTRIcon />}
                    iconAlign='right'
                    appearance='outline'
                    style={{
                      fontWeight: 400,
                      fontSize: '14px',
                      color: grey.z600,
                      backgroundColor: grey.z50,
                    }}
                    onClick={openPaymentUpdate}
                    disabled
                  >
                    Submit NEFT/RTGS UTR
                  </StyledButton>
                </Tooltip>
                <Tooltip
                  position='bottom'
                  label='There are no pending statements'
                  appearance='light'
                  size='big'
                  style={{
                    fontSize: '12px',
                    opacity: 0.8,
                    padding: '10px',
                    width: '100px',
                    marginLeft: '50px',
                  }}
                >
                  <StyledButton
                    icon={<OnlinePaymentsIcon />}
                    iconAlign='right'
                    onClick={openOnlinePaymentModal}
                    style={{
                      fontWeight: 400,
                      fontSize: '14px',
                    }}
                    disabled
                  >
                    Pay online
                  </StyledButton>
                </Tooltip>
              </div>
            ) : (
              <div>
                <StyledButton
                  icon={<ShareUTRIcon />}
                  iconAlign='right'
                  appearance='outline'
                  style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    color: red.z500,
                    backgroundColor: red.z50,
                  }}
                  onClick={openPaymentUpdate}
                >
                  Submit NEFT/RTGS UTR
                </StyledButton>
                <StyledButton
                  icon={<OnlinePaymentsIcon />}
                  iconAlign='right'
                  onClick={openOnlinePaymentModal}
                  style={{ fontWeight: 400, fontSize: '14px' }}
                >
                  Pay online
                </StyledButton>
              </div>
            )}
          </ActionComponentWrapper>
        )}
      </>
    );
  };
  // const ActionComponentV2 = () => {
  //   return <div />;
  // };

  if (!isOnlinePaymentsEnabled) {
    return (
      <DashboardWrapper>
        <NotFound />
      </DashboardWrapper>
    );
  }

  if (emptyPayments) {
    return (
      <DashboardWrapper>
        <EmptyStateV2
          title={emptyPaymentsStateTitle}
          state={PAYMENTS}
          actionComponent={ActionComponent}
        />
        {/* <PaymentUpdate
          closeModal={closePaymentUpdate}
          isVisible={paymentUpdateModal}
          heading='Submit UTR/Reference No.'
        /> */}
        <OnlinePaymentModal
          closeModal={closeOnlinePaymentModal}
          isVisible={onlinePaymentModal}
          heading='Pay online'
        />
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <BreadcrumbHeaderWrapper>
        <BreadCrumbWrapper>
          <Breadcrumb>
            <Breadcrumb.Link
              style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
              linkTo={DASHBOARD_ROUTE}
            >
              Home
            </Breadcrumb.Link>
            <Breadcrumb.Link
              style={{ color: 'var(--coolgreycoolgrey-800, #414A5D)' }}
              linkTo={PAYMENTS_ROUTE}
            >
              Payments
            </Breadcrumb.Link>
          </Breadcrumb>
        </BreadCrumbWrapper>
      </BreadcrumbHeaderWrapper>
      {currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT && (
        <>
          <TableHeaderWrapper>
            <H5>Consumption Statement(s)</H5>
            {/* <ActionComponent /> */}
          </TableHeaderWrapper>
          <TabWrapper>
            <Invoices setNoUnpaidInvocies={setNoUnpaidInvocies} />
          </TabWrapper>
        </>
      )}

      {currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT ? (
        <DebitsSectionHeaderWrapper>
          <H5>Payment History</H5>
        </DebitsSectionHeaderWrapper>
      ) : (
        <TableHeaderWrapper>
          <H5>
            <CreditsWrapper>Credits: </CreditsWrapper> Corporate top-ups
          </H5>
          <ActionComponent />
        </TableHeaderWrapper>
      )}
      <TabWrapper>
        <SearchContainerV2>
          <SearchInput
            leftIcon={<Search />}
            innerWidth='100%'
            value={searchCreditEnteries}
            onChange={onCreditInputChange}
            onFocus={() => setCreditIsSearchClicked(true)}
            onBlur={() => setCreditIsSearchClicked(false)}
            disabled={creditsSearchDisabled}
            label={
              isCreditSearchClicked || creditsSearchDisabled ? (
                'Search by UTR/reference no.'
              ) : (
                <span style={{ fontSize: '16px', fontWeight: 300 }}>
                  {' '}
                  Search by UTR/reference no.{' '}
                </span>
              )
            }
          />
          <StatusFilterAndExportCSVContainer>
            <StatusFilter
              isDisabled={creditsSearchDisabled}
              clearFilters={clearCreditFilters}
              currentFilters={currentCreditFilters}
              options={filterCreditStatusOptions}
              handleSelect={handleCreditStatusSelect}
            />
            {creditsSearchDisabled ? (
              <Download
                color='grey'
                style={{ padding: '10px 20px 20px 0px' }}
              />
            ) : (
              <Tooltip
                position='top'
                label='Download the payments list'
                appearance='light'
                size='big'
                style={{
                  fontSize: '12px',
                  opacity: 0.8,
                  marginRight: '10px',
                  marginLeft: '-30px',
                }}
              >
                <Download
                  color='grey'
                  onClick={exportCreditsSearchResultCSV}
                  style={downloadCreditsCSVStyle}
                />
              </Tooltip>
            )}
          </StatusFilterAndExportCSVContainer>
        </SearchContainerV2>
        <TableContainer>
          <DataTables
            columns={paymentsCreditsHeadColumn}
            loading={fetchingPayments || refreshingAuth}
            data={currentCreditsData}
            pagination
            totalRows={searchCreditsResults.length}
            handlePageChange={handleCreditsPageChange}
            paginationNumber={5}
            onSort={handleSortCredits}
            emptyStateTitle='You have not done any payment yet'
          />
        </TableContainer>
      </TabWrapper>
      {currentFlow === currentFlowTypeEnum.FLOW_TYPE_PPI && (
        <>
          <DebitsSectionHeaderWrapper>
            <H5>
              <DebitWrapper>Debits: </DebitWrapper> Employee top-ups
            </H5>
          </DebitsSectionHeaderWrapper>
          <TabWrapper>
            <SearchContainerV2>
              <SearchInput
                leftIcon={<Search />}
                innerWidth='100%'
                value={searchDebitEnteries}
                onChange={onDebitInputChange}
                onFocus={() => setDebitIsSearchClicked(true)}
                onBlur={() => setDebitIsSearchClicked(false)}
                label={
                  isDebitSearchClicked || debitsDisabledSearch ? (
                    'Search by reference no.'
                  ) : (
                    <span style={{ fontSize: '16px', fontWeight: 300 }}>
                      {' '}
                      Search by reference no.{' '}
                    </span>
                  )
                }
                disabled={debitsDisabledSearch}
              />
              <StatusFilterAndExportCSVContainer>
                <StatusFilter
                  isDisabled={debitsDisabledSearch}
                  clearFilters={clearDebitFilters}
                  currentFilters={currentDebitFilters}
                  options={filterDebitStatusOptions}
                  handleSelect={handleDebitStatusSelect}
                />
                {debitsDisabledSearch ? (
                  <Download color='grey' style={downloadDebitsCSVStyle} />
                ) : (
                  <Tooltip
                    position='top'
                    label='Download the debits list'
                    appearance='light'
                    size='big'
                    style={{
                      fontSize: '12px',
                      opacity: 0.8,
                      marginRight: '10px',
                      marginLeft: '-30px',
                    }}
                  >
                    <Download
                      color='grey'
                      onClick={exportDebitsSearchResultCSV}
                      style={downloadDebitsCSVStyle}
                    />
                  </Tooltip>
                )}
              </StatusFilterAndExportCSVContainer>
            </SearchContainerV2>
            <TableContainer>
              <DataTables
                columns={paymentsDebitsHeadColumn}
                loading={fetchingPaymentDebits || refreshingAuth}
                data={currentDebitsData}
                pagination
                totalRows={searchDebitsResults.length}
                handlePageChange={handleDebitsPageChange}
                paginationNumber={5}
                onSort={handleSortDebits}
                emptyStateTitle='No employee top-ups yet'
              />
            </TableContainer>
          </TabWrapper>
        </>
      )}
      <PaymentUpdate
        closeModal={closePaymentUpdate}
        isVisible={
          paymentUpdateModal &&
          currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT
        }
        heading='Submit UTR/Reference No.'
      />
      <OnlinePaymentModal
        closeModal={closeOnlinePaymentModal}
        isVisible={onlinePaymentModal && isOnlinePaymentsEnabled}
        heading='Pay online'
      />
    </DashboardWrapper>
  );
};

Payments.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  paymentsData: PropTypes.objectOf(PropTypes.any),
  paymentDebitsData: PropTypes.objectOf(PropTypes.any),
  getAllPayments: PropTypes.func,
  fetchingPayments: PropTypes.bool,
  getPaymentDebits: PropTypes.func,
  fetchingPaymentDebits: PropTypes.bool,
  isOnlinePaymentsEnabled: PropTypes.bool,
  showWarningToast: PropTypes.func,
  showSuccessToast: PropTypes.func,
  showFailedToast: PropTypes.func,
  refreshAuth: PropTypes.func,
  refreshingAuth: PropTypes.bool,
};

Payments.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  selectedWorkspace: {},
  paymentsData: {},
  paymentDebitsData: {},
  getAllPayments: _noop,
  fetchingPayments: true,
  fetchingPaymentDebits: true,
  getPaymentDebits: _noop,
  isOnlinePaymentsEnabled: false,
  showWarningToast: _noop,
  showSuccessToast: _noop,
  showFailedToast: _noop,
  refreshAuth: _noop,
  refreshingAuth: false,
};

export default Payments;
